import { useContext, useEffect, useState } from "@/common/hooks";
import { PrivateContext } from "@/routes/PrivateRoute";
import {
  AgGrid,
  ErrorMessage,
  MobileResponsiveGrid,
  Spinner,
  SelectDropdown,
  SuccessPopup,
  Button,
  FileInput,
  InfoPopup,
  CommonLink,
} from "@/common/components";
import {
  useSearchRecordKeeperNameMutation,
  useTransactionTypeQuery,
  useGetTransactionMatrixMutation,
  useSaveTransactionMatrixEntryTransactionMutation,
} from "@/service/ProductService";
import { parseNumber, yesNoFormatter } from "@/utils/Utilities";
import Papa from "papaparse";

const TransactionMatrix = () => {
  const [csvErrors, setCsvErrors] = useState(null);
  const [hideSelectRecordKeeper, setHideSelectRecordKeeper] = useState(false);
  const [hideSelectRecordKeeperErrMsg, setHideSelectRecordKeeperErrMsg] =
    useState(true);
  const [transactionTypeLocal, setTransactionType] = useState();
  const [showTable, setShowTable] = useState(false);
  const [showImportPopup, setShowImportPopup] = useState(false);
  const [attachment, setAttachment] = useState("");
  const [rowData, setRowData] = useState();
  const [rowDataClone, setRowDataClone] = useState();
  const [recordKeepers, setRecordKeepers] = useState();
  const [showPopup, setShowPopup] = useState(false);
  const [clonedRecordKeepers, setClonedRecordKeepers] = useState(null);
  const [gridApi, setGridApi] = useState(null);
  const [valueUpdated, setValueUpdated] = useState(false);
  const [showInfoPop, setShowInfoPop] = useState(false);
  const [isImported, setImported] = useState(false);
  const [fileInputKey, setFileInputKey] = useState(Date.now());
  const [hasUpdate, setHasUpdate] = useState(true);
  const [pagination, setPagination] = useState(false);
  const [recordKeeperSelected, setRecordKeeperSelected] = useState("");
  const {
    labels,
    setNavHeader,
    setPageHeader,
    productSponsorId,
    cloneDeep,
    location,
    hasAccessToUpdate,
    navigate,
  } = useContext(PrivateContext);

  const { data: transactionType } = useTransactionTypeQuery();
  const [
    saveTransactionMatrixEntryTransactionApi,
    { isLoading: isFetching, error: error },
  ] = useSaveTransactionMatrixEntryTransactionMutation();
  const [searchRecordKeeperNameApi, { isLoading: isFetching1, error: error1 }] =
    useSearchRecordKeeperNameMutation();
  const [getTransactionMatrixApi, { isLoading: isFetching2, error: error2 }] =
    useGetTransactionMatrixMutation();

  const columnDefs = [
    {
      headerName: labels?.transactionTable?.rKCode,
      field: "externalTransactionTypeId",
      type: "numericColumn",
      comparator: (a, b) => {
        const numA = parseNumber(a);
        const numB = parseNumber(b);
        if (isNaN(numA) && isNaN(numB)) return 0;
        if (isNaN(numA)) return 1;
        if (isNaN(numB)) return -1;
        return numA - numB;
      },
      width: 120,
    },
    {
      headerName: labels?.transactionTable?.rkDesc,
      field: "externalDescriptionText",
      width: 180,
    },
    {
      headerName: labels?.transactionTable?.riccCode,
      field: "transactionTypeId",
      comparator: (a, b) => {
        const numA = parseNumber(a);
        const numB = parseNumber(b);
        if (isNaN(numA) && isNaN(numB)) return 0;
        if (isNaN(numA)) return 1;
        if (isNaN(numB)) return -1;
        return numA - numB;
      },
      type: "numericColumn",
      width: 120,
      valueFormatter: (params) => {
        const value = params.value;
        return value !== null && value !== undefined
          ? value.toString().padStart(2, "0")
          : "";
      },
    },
    {
      headerName: labels?.transactionTable?.riccDescription,
      field: "riccDesc",
      width: 180,
    },
    {
      headerName: "Bal Ind",
      field: "balanceEffectCode",
      width: 100,
      valueFormatter: (params) => {
        switch (params.value) {
          case "A":
            return "Add";
          case "S":
            return "Subtract";
          case "N":
            return "No Effect";
          default:
            return params.value || "";
        }
      },
    },
    {
      headerName: labels?.transactionTable?.affectBase,
      field: "affectBaseCode",
      width: 150,
      valueFormatter: (params) => {
        switch (params.value) {
          case "Y":
            return "Yes";
          case "N":
            return "No";
          case "Z":
            return "Not Applicable";
          default:
            return params.value || "";
        }
      },
    },
    {
      headerName: labels?.transactionTable?.origAccum,
      field: "allowedCode0",
      width: 150,
      cellRenderer: getDropdownAllowedCodeRenderer(0, "Orig Accum"),
      valueFormatter: yesNoFormatter,
    },
    {
      headerName: labels?.transactionTable?.orugWithdrawal,
      field: "allowedCode1",
      width: 180,
      cellRenderer: getDropdownAllowedCodeRenderer(1, "Origin Withdrawal"),
      valueFormatter: yesNoFormatter,
    },
    {
      headerName: labels?.transactionTable?.beneAccum,
      field: "allowedCode2",
      width: 150,
      cellRenderer: getDropdownAllowedCodeRenderer(2, "Bene Accum"),
      valueFormatter: yesNoFormatter,
    },
    {
      headerName: labels?.transactionTable?.beneWithdrawal,
      field: "allowedCode3",
      width: 180,
      cellRenderer: getDropdownAllowedCodeRenderer(3, "Bene Withdrawal"),
      valueFormatter: yesNoFormatter,
    },
    {
      headerName: labels?.transactionTable?.qDROAccum,
      field: "allowedCode4",
      width: 150,
      cellRenderer: getDropdownAllowedCodeRenderer(4, "QDRO Accum"),
      valueFormatter: yesNoFormatter,
    },
    {
      headerName: labels?.transactionTable?.qDROWithdrawal,
      field: "allowedCode5",
      width: 180,
      cellRenderer: getDropdownAllowedCodeRenderer(5, "QDRO Withdrawal"),
      valueFormatter: yesNoFormatter,
    },
  ];

  const cancelLocal = () => {
    if (valueUpdated) {
      setShowInfoPop(true);
    } else {
      navigate(-1);
    }
  };

  const getMatrixData = (id) => {
    getTransactionMatrixApi({
      productSponsorId: productSponsorId,
      productId: location?.state?.productId,
      reckprReckpgSystemId: id,
    })
      .unwrap()
      .then((res) => {
        if (res.transactionMatrixEntries.length > 0) {
          processRowData(res.transactionMatrixEntries, true);
        } else {
          setShowTable(false);
          setHideSelectRecordKeeperErrMsg(false);
        }
      });
  };

  const processRowData = (data, fromApi) => {
    const processedData = data.map((element, index) => {
      const newElement = { ...element };
      newElement["id"] = index;
      newElement["riccDesc"] = element.transactionType.labelText;
      // Initialize a new array of the same length as the number of rules
      const orderedRules = [];

      element.transactionMatrixEntryRules.forEach((rule) => {
        const position = rule.ruleId - 1;
        orderedRules[position] = rule;
      });

      // Replace the original rules with the reordered array
      newElement.transactionMatrixEntryRules = orderedRules;

      // Optionally, extract allowedCodeX values for each rule
      orderedRules.forEach((rule, index) => {
        newElement[`allowedCode${index}`] = rule?.allowedCode ?? null;
      });

      return newElement;
    });
    setPagination(processedData.length > 50);
    if (fromApi) {
      setRowData(cloneDeep(processedData));
      setShowTable(true);
      setHideSelectRecordKeeperErrMsg(true);
    } else {
      setRowDataClone(cloneDeep(processedData));
    }
  };

  const getData = () => {
    searchRecordKeeperNameApi({
      productSponsorId: productSponsorId,
      productId: location?.state?.productId,
    })
      .unwrap()
      .then((res) => {
        setRecordKeepers(res);
        if (res.recordkeeperRecordkeepingSystems != null) {
          setHideSelectRecordKeeper(false);
          setRecordKeeperSelected(res.recordkeeperRecordkeepingSystems[0].id);
          getMatrixData(res.recordkeeperRecordkeepingSystems[0].id);
        } else {
          setHideSelectRecordKeeper(true);
        }
      });
  };

  function getDropdownAllowedCodeRenderer(index, ariaLabel) {
    return function DropdownRenderer(params) {
      const rowIndex = Number.isInteger(params?.rowIndex)
        ? params.rowIndex
        : params?.node?.rowIndex;

      if (rowIndex === undefined || rowIndex === null) {
        return null; // fallback or loading state if needed
      }

      const handleChange = (e) => {
        const updatedRows = [...rowData];
        const currentRow = { ...updatedRows[rowIndex] };

        // Defensive check
        if (
          !currentRow.transactionMatrixEntryRules ||
          !currentRow.transactionMatrixEntryRules[index]
        )
          return;

        currentRow.transactionMatrixEntryRules[index] = {
          ...currentRow.transactionMatrixEntryRules[index],
          allowedCode: e,
          modified: true,
        };

        currentRow[`allowedCode${index}`] = e;
        currentRow.modified = true;

        updatedRows[rowIndex] = currentRow;
        setRowData(updatedRows);

        // Tell AgGrid to update the row (prevents scroll jump)
        gridApi?.applyTransaction({
          update: [currentRow], // Must match getRowId for proper tracking
        });
        setValueUpdated(true);
      };

      return (
        <SelectDropdown
          id={`allowed-${index}-${rowIndex}`}
          dropdownData={[
            { id: "Y", name: "Yes" },
            { id: "N", name: "No" },
          ]}
          wrapperClassName="mb-0 py-2"
          selectClassName="w-100 py-2"
          select={handleChange}
          value={params.data?.[`allowedCode${index}`] ?? ""}
          optionId="id"
          optionLabel="name"
          defaultOption={false}
          ariaLabel={ariaLabel}
          disabled={!hasUpdate}
        />
      );
    };
  }

  const saveTransData = () => {
    if (isImported) {
      saveTransDataImport(rowData);
      return;
    }
    if (!valueUpdated) {
      return;
    }
    const cleanedEntries = cloneDeep(rowData)
      .filter((entry) => entry.modified) //  only changed rows
      .map(
        ({
          // eslint-disable-next-line no-unused-vars
          allowedCode0,
          transactionMatrixEntryRules,
          ...rest
        }) => ({
          ...rest,
          transactionMatrixEntryRules: transactionMatrixEntryRules?.map(
            ({ productSponsorId, ...ruleRest }) => ({
              ...ruleRest,
              productSponsorId: productSponsorId,
            })
          ),
        })
      );

    const payload = {
      productId: location?.state?.productId,
      productSponsorId: productSponsorId,
      reckprReckpgSystemId: recordKeeperSelected,
      isImportFile: false,
      transactionMatrixEntries: cleanedEntries,
    };

    saveTransactionMatrixEntryTransactionApi(payload)
      .unwrap()
      .then((res) => {
        if (!res.inError) {
          setShowPopup(true);
          rowData.forEach((entry) => {
            delete entry.modified;
            entry.transactionMatrixEntryRules.forEach(
              (rule) => delete rule.modified
            );
          });
          setRowData([...rowData]);
        }
      });
  };

  const keyPart = (v) => (v == null ? "" : String(v).trim());

  // Ensure addMapCode ∈ {"D","P",""} 
  // If value is null or empty, pass it through as is.
  const assignDeferralForCollisions = (beans = []) => {
    return (beans || []).map((b) => {
      const inputDeferralCode = b.addMapCode;

      // 1. If null or empty value comes in the request, pass it as is
      if (inputDeferralCode === null || inputDeferralCode === undefined || inputDeferralCode === "") {
        return { ...b, addMapCode: inputDeferralCode };
      }

      // 2. For non-empty values, validate they are "D" or "P"
      let code = keyPart(inputDeferralCode).toUpperCase();
      if (code !== "D" && code !== "P") {
        code = "D"; // Default to "D" only if a non-empty invalid value is provided
      }

      return { ...b, addMapCode: code };
    });
  };


  const saveTransDataImport = (data) => {
    const cleanedEntries = cloneDeep(data).map(
      ({
        transactionMatrixEntryRules,
        transactionType,
        affectBaseCode,
        balanceEffectCode,
        externalDescriptionText,
        addMapCode,
        reckprReckpgSystemId,
      }) => ({
        transactionMatrixEntryRules: transactionMatrixEntryRules?.map(
          ({ allowedCode, productPhaseCode, productOwnershipTypeCode }) => ({
            allowedCode,
            productPhaseCode,
            productOwnershipTypeCode,
          })
        ),
        transactionTypeId: transactionType?.transactionTypeId || null,
        externalTransactionTypeId: reckprReckpgSystemId || null,
        externalDescriptionText: externalDescriptionText || null,
        affectBaseCode: affectBaseCode || null,
        balanceEffectCode: balanceEffectCode || "N",
        addMapCode: addMapCode || null, // <-- include it
      })
    );

    let entries = cleanedEntries;

    // Assign D/P only where needed to prevent DB collisions
    try {
      entries = assignDeferralForCollisions(entries);
    } catch (e) {
      setCsvErrors({ errorMessage: e.message });
      return;
    }

    const payload = {
      productId: location?.state?.productId,
      productSponsorId: productSponsorId,
      reckprReckpgSystemId: recordKeeperSelected,
      isImportFile: true,
      transactionMatrixEntries: entries,
    };

    saveTransactionMatrixEntryTransactionApi(payload)
      .unwrap()
      .then((res) => {
        if (!res.inError) {
          setShowPopup(true);
        }
      });
  };

  const handleCsvFileChange = (files) => {
    setCsvErrors(null);
    setAttachment(files);
    importCsv(files);
  };

  const importCsv = (files) => {
    setCsvErrors(null);

    const file = files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".csv")) {
      setCsvErrors({
        errorMessage: labels?.transactionTable?.invalidFileError,
      });
      return;
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        let importPayload = transformMatrixEntries(
          results.data,
          transactionTypeLocal.transactionTypes
        );
        setShowImportPopup(false);

        if (importPayload.length != 0) {
          processRowData(importPayload, false);
          setImported(true);
          setValueUpdated(true);
        }
      },
    });
  };

  const getRuleID = (code) => {
    switch (code) {
      case "Original - Accumulation":
        return "1";
      case "Original - Withdrawal":
        return "2";
      case "Beneficiary - Accumulation":
        return "3";
      case "Beneficiary - Withdrawal":
        return "4";
      case "QDRO - Accumulation":
        return "5";
      case "QDRO - Withdrawal":
        return "6";
      default:
        return null; // or throw an error, or return 0 if you prefer
    }
  };

  const transformMatrixEntries = (
    inputData,
    transactionTypeList,
    strictMode = false
  ) => {
    const PRODUCT_SPONSOR_ID = productSponsorId;
    const PRODUCT_ID = location?.state?.productId;
    const LAST_MNT_OP_ID = "";
    const IMPORT_ERROR =
      "File selected for import does not contain all required information.";

    const nowTimestamp = () =>
      new Date().toISOString().replace("T", " ").replace("Z", "");

    const RULE_MATRIX = [
      { ownership: "O", phase: "A" },
      { ownership: "O", phase: "W" },
      { ownership: "B", phase: "A" },
      { ownership: "B", phase: "W" },
      { ownership: "Q", phase: "A" },
      { ownership: "Q", phase: "W" },
    ];

    const validateField = (value, expr, maxLength = 20) => {
      const trimmed = (value || "").trim();
      if (trimmed.length > maxLength) return null;
      return expr.test(trimmed) ? trimmed : null;
    };

    const seenKeys = new Set();
    let transformed = [];
    let hasCharacterExceeded = false;
    inputData.forEach((item, index) => {
      try {
        const deferralCode = (item["Deferral"] || "").trim();
        if (deferralCode.toLowerCase() === "n") {
          return; // skip
        }

        const NORMAL_EXPR = /^\S.{0,19}$/im;
        const BAL_IND_EXPR = /^[asn]$/im;
        const AFC_BAS_EXPR = /^[ynz]$/im;
        const YES_NO_EXPR = /^[yn]$/im;
        // LENGTH VALIDATION BREAKS ENTIRE LOOP
        const FIELDS_TO_CHECK = [
          {
            label: "RICC Code",
            value: item["RICC Code"],
            validator: NORMAL_EXPR,
          },
          {
            label: "R/K Code",
            value: item["R/K Code"],
            validator: NORMAL_EXPR,
          },
          {
            label: "R/K Description",
            value: item["R/K Description"],
            validator: NORMAL_EXPR,
          },
          { label: "Bal Ind", value: item["Bal Ind"], validator: BAL_IND_EXPR },
          {
            label: "Affect Base",
            value: item["Affect Base"],
            validator: AFC_BAS_EXPR,
          },
          {
            label: "Original - Accumulation",
            value: item["Original - Accumulation"],
            validator: YES_NO_EXPR,
          },
          {
            label: "Original - Withdrawal",
            value: item["Original - Withdrawal"],
            validator: YES_NO_EXPR,
          },
          {
            label: "Beneficiary - Accumulation",
            value: item["Beneficiary - Accumulation"],
            validator: YES_NO_EXPR,
          },
          {
            label: "Beneficiary - Withdrawal",
            value: item["Beneficiary - Withdrawal"],
            validator: YES_NO_EXPR,
          },
          {
            label: "QDRO - Accumulation",
            value: item["QDRO - Accumulation"],
            validator: YES_NO_EXPR,
          },
          {
            label: "QDRO - Withdrawal",
            value: item["QDRO - Withdrawal"],
            validator: YES_NO_EXPR,
          },
        ];

        for (const field of FIELDS_TO_CHECK) {
          const val = (field.value || "").toString().trim();

          if (!field.validator.test(val)) {
            hasCharacterExceeded = true;
          }
        }

        // Required fields
        const transactionTypeId = validateField(item["RICC Code"], NORMAL_EXPR);
        const reckpgSystemId = validateField(item["R/K Code"], NORMAL_EXPR);
        const labelText = validateField(item["R/K Description"], NORMAL_EXPR);
        const affectBase = validateField(item["Affect Base"], AFC_BAS_EXPR);
        const balanceEffectCode = validateField(item["Bal Ind"], BAL_IND_EXPR);

        if (
          !transactionTypeId ||
          !reckpgSystemId ||
          !labelText ||
          !affectBase ||
          !balanceEffectCode
        ) {
          const msg = `Row ${index + 1}: Required field invalid`;
          if (strictMode) throw new Error(msg);
          return;
        }

        const allowedFields = [
          "Original - Accumulation",
          "Original - Withdrawal",
          "Beneficiary - Accumulation",
          "Beneficiary - Withdrawal",
          "QDRO - Accumulation",
          "QDRO - Withdrawal",
        ];

        const allAllowedCodesValid = allowedFields.every((field) => {
          return validateField(item[field], YES_NO_EXPR) !== null;
        });

        if (!allAllowedCodesValid) {
          const msg = `Row ${index + 1}: Invalid allowed code`;
          if (strictMode) throw new Error(msg);
          return;
        }

        const rkDefKey = reckpgSystemId + deferralCode;
        if (seenKeys.has(rkDefKey)) {
          const msg = `Row ${index + 1}: Duplicate R/K Code + Deferral`;
          if (strictMode) throw new Error(msg);
          return;
        }
        seenKeys.add(rkDefKey);

        const matchedTransactionType = transactionTypeList.find(
          (tt) => tt.transactionTypeId === transactionTypeId
        );
        if (!matchedTransactionType) {
          const msg = `Row ${index + 1}: Transaction type not found`;
          if (strictMode) throw new Error(msg);
          return;
        }

        const entryId = index.toString();

        const ruleEntries = RULE_MATRIX.map((rule, i) => {
          const allowedCode = allowedFields[i];
          return {
            errors: [],
            productSponsorId: PRODUCT_SPONSOR_ID,
            productId: PRODUCT_ID,
            reckprReckpgSystemId: reckpgSystemId,
            entryId,
            ruleId: getRuleID(allowedCode),
            lastMntOpId: LAST_MNT_OP_ID,
            lastMntDateTime: nowTimestamp(),
            productOwnershipTypeCode: rule.ownership,
            productPhaseCode: rule.phase,
            allowedCode: (item[allowedCode] || "N").trim(),
            inError: false,
          };
        });

        const transformedItem = {
          errors: [],
          productSponsorId: PRODUCT_SPONSOR_ID,
          productId: PRODUCT_ID,
          reckprReckpgSystemId: reckpgSystemId,
          entryId,
          lastMntOpId: LAST_MNT_OP_ID,
          lastMntDateTime: nowTimestamp(),
          externalTransactionTypeId: reckpgSystemId, // assumed static
          transactionTypeId,
          addMapCode: deferralCode || null,
          balanceEffectCode,
          affectBaseCode: affectBase,
          externalDescriptionText: labelText,
          transactionMatrixEntryRules: ruleEntries,
          transactionType: matchedTransactionType,
          inError: false,
          allowedCode0: item["Original - Accumulation"] || "N",
          allowedCode4: item["Original - Withdrawal"] || "N",
          allowedCode2: item["Beneficiary - Accumulation"] || "N",
          allowedCode1: item["Beneficiary - Withdrawal"] || "N",
          allowedCode5: item["QDRO - Accumulation"] || "N",
          allowedCode3: item["QDRO - Withdrawal"] || "N",
        };

        // Optional: Non-Spouse Bene if fields are present
        if (
          "Non-Spouse Bene - Accumulation" in item &&
          "Non-Spouse Bene - Withdrawal" in item
        ) {
          const nonSpouseValid =
            validateField(
              item["Non-Spouse Bene - Accumulation"],
              YES_NO_EXPR
            ) &&
            validateField(item["Non-Spouse Bene - Withdrawal"], YES_NO_EXPR);

          if (!nonSpouseValid) {
            const msg = `Row ${index + 1}: Invalid Non-Spouse Bene value`;
            if (strictMode) throw new Error(msg);
            return;
          }

          transformedItem.allowedCode6 =
            item["Non-Spouse Bene - Accumulation"] || "N";
          transformedItem.allowedCode7 =
            item["Non-Spouse Bene - Withdrawal"] || "N";
        }

        transformed.push(transformedItem);
      } catch (err) {
        if (strictMode) throw new Error(IMPORT_ERROR);
        return err;
      }
    });

    if (hasCharacterExceeded) {
      transformed = [];
    }

    if (transformed.length === 0) {
      let error = {};
      error["errorMessage"] = IMPORT_ERROR;
      setCsvErrors(error);
      return [];
    }

    return transformed;
  };

  useEffect(() => {
    setTransactionType(transactionType);
  }, [transactionType]);

  useEffect(() => {
    if (recordKeepers?.recordkeeperRecordkeepingSystems) {
      const clone = cloneDeep(recordKeepers);
      clone.recordkeeperRecordkeepingSystems.forEach((system) => {
        system.name =
          system.recordkeeperName + " - " + system.recordkeepingName;
      });
      setClonedRecordKeepers(clone);
    }
  }, [recordKeepers]);

  useEffect(() => {
    setPageHeader(
      labels?.transactionTable?.title + " " + location?.state?.name
    );
    setNavHeader(labels?.navbar?.transactionMatrix);
    getData();
    setHasUpdate(hasAccessToUpdate());
  }, []);

  return (
    <section className="container-x-large py-5 ">
      {(isFetching || isFetching1 || isFetching2) && <Spinner />}
      <ErrorMessage error={error || error1 || error2} wrapperClassName="mb-3" />
      <div className="transactionBody">
        {!hideSelectRecordKeeper && (
          <>
            <div>
              <label className="form-label fw-bold">
                {labels?.transactionTable?.recordKeeper}
              </label>
            </div>
            <div className="">
              <div className="col-md-4 mb-2">
                <SelectDropdown
                  id="recordKeeper"
                  label={""}
                  wrapperClassName=""
                  dropdownData={
                    clonedRecordKeepers?.recordkeeperRecordkeepingSystems
                  }
                  selectClassName="w-100 py-2"
                  select={(e) => {
                    setRecordKeeperSelected(e);
                    getMatrixData(e);
                  }}
                  value={recordKeeperSelected}
                  optionId="id"
                  optionLabel="companyName"
                  ariaLabel="RecordKeeper"
                />
              </div>

              <div>
                <CommonLink wrapperClassName="mb-3" disabled={!hasUpdate}>
                  <span
                    onClick={() => {
                      setShowImportPopup(true);
                    }}
                  >
                    {labels?.transactionTable?.importTransactionTable}
                  </span>
                </CommonLink>
              </div>
            </div>
          </>
        )}

        <div className="">
          <InfoPopup
            heading={labels?.transactionTable?.importTransactionFile}
            showModal={showImportPopup}
            primaryButtonText="Import"
            primaryButtonClick={() => {
              if (csvErrors != null) return;
              setRowData(cloneDeep(rowDataClone));
              setShowTable(true);
              setHideSelectRecordKeeperErrMsg(true);
              setAttachment([]);
              setFileInputKey(Date.now());
            }}
            primaryButtonDisabled={
              !attachment?.length || csvErrors?.errorMessage
            }
            secondaryButtonText="Cancel"
            secondaryButtonClick={() => {
              setAttachment([]);
              setCsvErrors(null);
              setShowImportPopup(false);
              setFileInputKey(Date.now());
            }}
            body={
              <>
                <FileInput
                  key={fileInputKey}
                  value={attachment}
                  accept=".csv"
                  id="file-attachment"
                  onChange={(e) => {
                    handleCsvFileChange(e);
                  }}
                />

                {csvErrors != null && (
                  <>
                    <ErrorMessage error={csvErrors} wrapperClassName="mb-3" />
                  </>
                )}
              </>
            }
          />
        </div>

        {showInfoPop && (
          <div className="mb-3">
            <InfoPopup
              heading={labels?.transactionTable?.changesUpdate}
              showModal={showInfoPop}
              primaryButtonText="Yes"
              primaryButtonClick={() => {
                navigate(-1);
              }}
              secondaryButtonText="Cancel"
              secondaryButtonClick={() => {
                setShowInfoPop(false);
              }}
            />
          </div>
        )}
      </div>
      {showTable && (
        <div style={{ width: "100%" }} className="transcitionTable">
          <AgGrid
            getRowId={(params) => String(params.data.id)}
            onGridReady={(params) => setGridApi(params.api)}
            wrapperClassNames="mb-5 d-md-block d-none"
            className="ag-theme-alpine ag-no-header-wrap "
            showPrintTable={false}
            columnDefs={columnDefs}
            rowData={rowData}
            domLayout="autoHeight"
            agGridClassNames="w-1400"
            defaultColDef={{
              headerClass: "ag-no-ellipsis",
              sortable: true,
            }}
            pagination={pagination}
            paginationPageSize={50}
          />

          <MobileResponsiveGrid
            rowData={rowData}
            columnDefs={columnDefs}
            showSortDropDown={false}
          />
        </div>
      )}
      {hideSelectRecordKeeper && (
        <ErrorMessage
          error={{
            errorMessage: labels?.transactionTable?.noRecordKeeper,
          }}
        />
      )}
      {!showTable && !hideSelectRecordKeeperErrMsg && (
        <ErrorMessage
          error={{
            errorMessage: labels?.transactionTable?.noData,
          }}
        />
      )}
      {showTable && (
        <div className="d-flex gap-1 flex-md-row justify-content-end mt-3">
          <Button
            id="cancel"
            text={labels?.transactionTable?.cancel}
            type="secondary"
            wrapperClassName="col-md-2 col-6"
            click={cancelLocal}
          />

          <Button
            id="Done"
            text={labels?.transactionTable?.done}
            wrapperClassName="col-md-2 col-6"
            disabled={!valueUpdated && !hasUpdate}
            click={saveTransData}
          />
        </div>
      )}
      <SuccessPopup
        showModal={showPopup}
        buttonText={labels?.transactionTable?.continue}
        body={labels?.transactionTable?.continueMessage}
        click={() => navigate(-1)}
      />
    </section>
  );
};

export default TransactionMatrix;
